import { X } from "lucide-react";
import React from "react";
import ReactModal from "react-modal";
import { useDeviceContext } from "../../../../context/DeviceContext";
import { EDSButton } from "../../../eds/EDSButton";

type DetachedModalType = {
  heading: string | React.ReactNode;
  width: string;
  height?: React.CSSProperties["height"];
  handleClose: () => void;
  children?: React.ReactNode;
};

const DetachedModal = ({
  heading,
  width,
  height,
  handleClose,
  children
}: DetachedModalType) => {
  const { theme } = useDeviceContext();
  const modalStyles: ReactModal.Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.4)",
      // Z-INDEX HIERARCHY DOCUMENTATION:
      // This modal uses z-index: 10000 to ensure dropdowns can appear above it.
      // The z-index was previously reduced from 50000 to 10000 because dropdowns
      // were opening behind the modal. Keep at 10000 unless there's a specific
      // reason to change it.
      //
      // Current z-index values in codebase:
      // - General UI elements: 1-100 (cards, buttons, badges)
      // - Popovers/Tooltips: 1000-3000 (Radix UI popovers, tooltips)
      // - Time picker fixes: 9999-10000 (time-picker-fix.css, radix-positioning-fixes.css)
      // - MainMenu: 10000 (MainMenu.css)
      // - DetachedModal overlay: 10000 (this file) - REVERTED from 50000
      // - DialogOverlay: 10000 (ui/dialog.tsx)
      // - DialogContent: 30000 (ui/dialog.tsx, eds-component-overrides.css)
      // - Select component: 50001 (ui/select.tsx) - dropdowns inside modals
      // - DiaryNoteModal: 50000 (DiaryNoteModal.scss)
      // - CreateEditClosedArea: 50000 (legacy.css)
      // - DiaryMain popover overlay: 10000 (DiaryMain.scss)
      // - WebViewPopover dropdown: 100000 (eds-component-overrides.css)
      // - WebViewPopover max: 2147483647 (WebViewPopover.tsx) - maximum possible
      //
      // Note: Dropdowns inside this modal (e.g., Select components) use z-index 50001
      // to ensure they appear above the modal overlay (10000) and content.
      zIndex: 10000,
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    },
    content: {
      backgroundColor: "var(--panelBackground)",
      width,
      height: height ?? ("fit-content" as React.CSSProperties["height"]),
      minHeight: "380px",
      maxHeight: "calc(100vh - 80px)",
      margin: "auto",
      borderRadius: "6px",
      border: theme !== "Light" ? "1px solid var(--borderColor)" : "none",
      overflowY: "auto",
      overflowX: "visible",
      padding: "24px 20px",
      boxShadow: "0 3px 16px 0px rgb(0,0,0,0.16)",
      position: "absolute",
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      transform: "translate(-50%, -50%)"
      // Note: Content inherits z-index from overlay's stacking context
      // No explicit z-index needed as it's a child of the overlay
    }
  };

  return (
    <ReactModal
      onAfterClose={handleClose}
      isOpen={true}
      ariaHideApp={false}
      style={modalStyles}
      shouldReturnFocusAfterClose={true}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      portalClassName="DetachedModalPortal"
      parentSelector={() => document.body}
    >
      <div className="ModalContainer h-auto min-h-full flex flex-col">
        <div className="ModalHeading text-collins-heading font-bold text-foreground flex items-center justify-between w-full mb-4 shrink-0">
          <span>{heading}</span>
          <EDSButton
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Close"
          >
            <X />
          </EDSButton>
        </div>
        <div className="ModalScrollableContent flex-1 overflow-y-auto overflow-x-hidden flex flex-col min-h-0">
          {children}
        </div>
      </div>
    </ReactModal>
  );
};

export default React.memo<DetachedModalType>(DetachedModal);
